import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AuthService } from 'app/um/services/auth-service';
import { LookupService } from 'app/core/services/lookup-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { I18N } from 'aurelia-i18n';
import { getReference } from 'app/shared/models/essette-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveTurnAroundTime extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    protected turnAroundTime: UM.AuthServiceTurnAroundTime | null = null;

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected filteredStatuses: ReadonlyArray<UM.AuthStatus> = [];

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected baseStatuses: ReadonlyArray<UM.AuthStatus> = [];

    protected unitTexts: { text: string, unit: string }[] = [];
    protected selectedUnit?: { text: string, unit: string };

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        protected lookupService: LookupService,
        private readonly toaster: Toaster,
        protected i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.unitTexts = [
            { text: this.i18n.tr('ui:timespans.businessDays.$', { count: 2 }), unit: 'B' },
            { text: this.i18n.tr('ui:timespans.businessDaysWSaturday.$', { count: 2 }), unit: 'S' },
            { text: this.i18n.tr('ui:timespans.businessHours.$', { count: 2 }), unit: 'R' },
            { text: this.i18n.tr('ui:timespans.days.$', { count: 2 }), unit: 'C' }
        ];

        this.loadBusinessHourSettings();
    }

    ////****************** Events ***************************************************////
    protected turnAroundTimeChanged(): void {
        this.selectedUnit = this.unitTexts.find(ut => ut.unit === this.turnAroundTime?.unit);
    }

    ////****************** Functions ************************************************////
    protected constructSaveObject(): UM.AuthServiceTurnAroundTime {
        const saveTurnAroundTime: UM.AuthServiceTurnAroundTime = {
            lineOfBusiness: this.turnAroundTime?.lineOfBusiness,
            type: this.turnAroundTime?.type,
            priority: this.turnAroundTime?.priority,
            status: getReference(this.turnAroundTime?.status),
            baseStatus: getReference(this.turnAroundTime?.baseStatus),
            unit: this.selectedUnit?.unit,
            value: this.turnAroundTime?.value
        };

        // only add id if this is a record edit (-1 is the dummy record ID)
        if(this.turnAroundTime?.id && this.turnAroundTime.id !== '-1') {
            saveTurnAroundTime.id = this.turnAroundTime.id;
        }

        return saveTurnAroundTime;
    }

    public reset(): void {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.saveTurnAroundTime();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadBusinessHourSettings(): Promise<void> {
        let businessHoursConfigured = false;
        try {
            this.state.beginLoad();
            const { settings } = await this.lookupService.loadBusinessHourSettings();
            businessHoursConfigured = !!getApiSetting(settings, 'businessHours.$configured');
        } catch(error) {
            throw error;
        } finally {
            // if business hours are not configured, remove Business Hours as an option from the Time Span dropdown
            if(!businessHoursConfigured) this.unitTexts = this.unitTexts.filter(unitText => unitText.unit !== 'R');
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveTurnAroundTime(): Promise<boolean> {
        if(!this.turnAroundTime) return false;

        let isSuccessful = false;
        const saveTat = this.constructSaveObject();

        try {
            this.state.beginSave();
            const response = await this.service.saveAuthServiceTurnAroundTime(saveTat);
            isSuccessful = response.isSuccessful;

            if(isSuccessful) {
                this.turnAroundTime.id = response.id;
                this.turnAroundTime.unit = saveTat.unit;
            }

            return isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:auth.turnAroundTime.$long', isSuccessful);
            this.state.endSave();
        }
    }
}
